// === EVENT MANAGER — Golden Cookies / Random Events ===

export type EventEffect =
  | 'production_gold'
  | 'production_food'
  | 'production_faith_knowledge'
  | 'production_all'
  | 'cost_discount'
  | 'raid_bonus';

export interface EventDef {
  id: string;
  name: string;
  icon: string;
  effect: EventEffect;
  multiplier: number;
  duration: number; // seconds; 0 = instant
  weight: number;
}

export interface PendingEvent {
  type: EventDef;
  expiresIn: number; // seconds remaining before the pending popup expires
}

export interface ActiveEvent {
  type: EventDef;
  remainingSeconds: number;
  duration: number;
}

// ---------------------------------------------------------------------------
// Event catalogue
// ---------------------------------------------------------------------------

export const EVENT_DEFS: readonly EventDef[] = [
  {
    id: 'gold_rush',
    name: 'Gold Rush',
    icon: '🪙',
    effect: 'production_gold',
    multiplier: 2,
    duration: 30,
    weight: 25,
  },
  {
    id: 'harvest',
    name: 'Harvest',
    icon: '🌾',
    effect: 'production_food',
    multiplier: 2,
    duration: 30,
    weight: 25,
  },
  {
    id: 'enlightenment',
    name: 'Enlightenment',
    icon: '✨',
    effect: 'production_faith_knowledge',
    multiplier: 2,
    duration: 30,
    weight: 15,
  },
  {
    id: 'frenzy',
    name: 'Frenzy',
    icon: '⚡',
    effect: 'production_all',
    multiplier: 3,
    duration: 15,
    weight: 10,
  },
  {
    id: 'discount',
    name: 'Discount',
    icon: '🏷️',
    effect: 'cost_discount',
    multiplier: 0.5,
    duration: 45,
    weight: 15,
  },
  {
    id: 'raid_bonus',
    name: 'Raid Bonus',
    icon: '⚔️',
    effect: 'raid_bonus',
    multiplier: 1,
    duration: 0,
    weight: 10,
  },
] as const;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MIN_SPAWN = 300; // seconds
const MAX_SPAWN = 900; // seconds
const PENDING_TIMEOUT = 10; // seconds the popup stays before expiring

// ---------------------------------------------------------------------------
// Weighted random selection
// ---------------------------------------------------------------------------

function pickWeightedEvent(): EventDef {
  const totalWeight = EVENT_DEFS.reduce((sum, e) => sum + e.weight, 0);
  let roll = Math.random() * totalWeight;
  for (const def of EVENT_DEFS) {
    roll -= def.weight;
    if (roll <= 0) return def;
  }
  // Fallback (should never happen)
  return EVENT_DEFS[EVENT_DEFS.length - 1]!;
}

function randomSpawnDelay(): number {
  return MIN_SPAWN + Math.random() * (MAX_SPAWN - MIN_SPAWN);
}

// ---------------------------------------------------------------------------
// EventManager
// ---------------------------------------------------------------------------

export class EventManager {
  private spawnTimer: number; // seconds until next event spawns
  private pendingEvent: PendingEvent | null = null;
  private activeEvent: ActiveEvent | null = null;
  private tabVisible = true;
  private freeRaidAvailable = false;
  private spawnSpeedReduction: number = 0; // from event_speed talent

  constructor() {
    this.spawnTimer = randomSpawnDelay();
  }

  setSpawnSpeedReduction(reduction: number): void {
    this.spawnSpeedReduction = reduction;
  }

  // ---------------------------------------------------------------------------
  // Per-tick update
  // ---------------------------------------------------------------------------

  update(deltaSeconds: number): void {
    // Decrement active event timer
    if (this.activeEvent !== null) {
      this.activeEvent.remainingSeconds -= deltaSeconds;
      if (this.activeEvent.remainingSeconds <= 0) {
        this.activeEvent = null;
      }
    }

    // Decrement pending event expiry
    if (this.pendingEvent !== null) {
      this.pendingEvent.expiresIn -= deltaSeconds;
      if (this.pendingEvent.expiresIn <= 0) {
        this.pendingEvent = null;
      }
    }

    // Decrement spawn timer — only advances when tab is visible and no pending/active
    if (this.tabVisible && this.pendingEvent === null && this.activeEvent === null) {
      this.spawnTimer -= deltaSeconds;
      if (this.spawnTimer <= 0) {
        this.pendingEvent = {
          type: pickWeightedEvent(),
          expiresIn: PENDING_TIMEOUT,
        };
        this.spawnTimer = randomSpawnDelay() * (1 - Math.min(this.spawnSpeedReduction, 0.6));
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Activate the pending event (player tapped the popup)
  // ---------------------------------------------------------------------------

  activate(): void {
    if (this.pendingEvent === null) return;

    const def = this.pendingEvent.type;
    this.pendingEvent = null;

    if (def.effect === 'raid_bonus') {
      // Instant — no duration
      this.freeRaidAvailable = true;
      return;
    }

    this.activeEvent = {
      type: def,
      remainingSeconds: def.duration,
      duration: def.duration,
    };
  }

  // ---------------------------------------------------------------------------
  // Page Visibility API gate
  // ---------------------------------------------------------------------------

  setTabVisible(visible: boolean): void {
    this.tabVisible = visible;
  }

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  getPendingEvent(): PendingEvent | null {
    return this.pendingEvent;
  }

  getActiveEvent(): ActiveEvent | null {
    return this.activeEvent;
  }

  /**
   * Returns the production multiplier for a given resource key,
   * or the global multiplier when resource is omitted.
   * Returns 1 if no event is active or the event doesn't affect this resource.
   */
  getProductionMultiplier(resource?: 'gold' | 'food' | 'faith' | 'knowledge'): number {
    if (this.activeEvent === null) return 1;

    const { effect, multiplier } = this.activeEvent.type;

    switch (effect) {
      case 'production_all':
        return multiplier;
      case 'production_gold':
        if (resource === undefined || resource === 'gold') return multiplier;
        return 1;
      case 'production_food':
        if (resource === undefined || resource === 'food') return multiplier;
        return 1;
      case 'production_faith_knowledge':
        if (resource === undefined || resource === 'faith' || resource === 'knowledge') return multiplier;
        return 1;
      default:
        return 1;
    }
  }

  /**
   * Returns the cost multiplier (< 1 means cheaper).
   * Returns 1 if no discount event is active.
   */
  getCostMultiplier(): number {
    if (this.activeEvent === null) return 1;
    if (this.activeEvent.type.effect === 'cost_discount') {
      return this.activeEvent.type.multiplier;
    }
    return 1;
  }

  /**
   * Consumes the free raid granted by the raid_bonus event.
   * Returns true if a free raid was available.
   */
  consumeFreeRaid(): boolean {
    if (!this.freeRaidAvailable) return false;
    this.freeRaidAvailable = false;
    return true;
  }
}
